"use client";

/**
 * NavbarComponent
 * Navigation bar for landing pages with dropdown and secondary nav support
 */

import { LandingPageComponent, NavbarConfig, NavLink } from "@/lib/database/landing-pages";
import { generateColorPalette, DEFAULT_PRIMARY_COLOR } from "../../../_logic/color-utils";
import { Menu, X, ChevronDown } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import SmartButton from "../../shared/SmartButton";
import { FetchButton } from "@/fetch-ui/FetchButton";

interface NavbarComponentProps {
  component: LandingPageComponent;
  eventId: number;
  primaryColor?: string;
}

function handleNavClick(e: React.MouseEvent<HTMLAnchorElement>, href: string, hasSecondaryNav: boolean) {
  e.preventDefault();
  if (!href) return;
  if (href.startsWith("#")) {
    const id = href.slice(1);
    const el = document.getElementById(id);
    if (el) {
      // 64px navbar + 40px secondary nav if present + 16px padding
      const offset = hasSecondaryNav ? 120 : 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
      window.history.replaceState(null, "", window.location.pathname.replace(/\/$/, "") + href);
    }
  } else {
    window.location.href = href;
  }
}

function NavDropdown({ link, hasSecondaryNav }: { link: NavLink; hasSecondaryNav: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleMouseEnter = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => setIsOpen(false), 150);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <div ref={dropdownRef} className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {link.headerOnly || !link.href ? (
        <button
          type="button"
          className="flex items-center gap-1 text-gray-600 hover:text-gray-900 font-medium cursor-pointer"
          onClick={() => setIsOpen(!isOpen)}
        >
          {link.label}
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </button>
      ) : (
        <a
          href={link.href}
          className="flex items-center gap-1 text-gray-600 hover:text-gray-900 font-medium"
          onClick={(e) => {
            e.preventDefault();
            if (!isOpen) {
              setIsOpen(true);
            } else {
              setIsOpen(false);
              handleNavClick(e, link.href, hasSecondaryNav);
            }
          }}
        >
          {link.label}
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </a>
      )}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[180px] z-50">
          {!link.headerOnly && link.href && (
            <>
              <a
                href={link.href}
                className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900 font-medium"
                onClick={(e) => {
                  setIsOpen(false);
                  handleNavClick(e, link.href, hasSecondaryNav);
                }}
              >
                {link.label}
              </a>
              <div className="border-t border-gray-100" />
            </>
          )}
          {(link.children || []).map((child, index) => (
            <a
              key={index}
              href={child.href || "#"}
              className="block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-gray-900"
              onClick={(e) => {
                e.preventDefault();
                setIsOpen(false);
                if (child.href) {
                  handleNavClick(e, child.href, hasSecondaryNav);
                }
              }}
            >
              {child.label}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

export default function NavbarComponent({ component, eventId, primaryColor }: NavbarComponentProps) {
  const config = (component.config || {}) as NavbarConfig;
  const searchParams = useSearchParams();
  const navLogoOverride = searchParams.get("navLogo");
  const logoImage = navLogoOverride || config.logoImage || "";
  const autoLinks: NavLink[] = (config.links || []).filter((link) => link.visible !== false);
  const customLinks: NavLink[] = (config.customLinks || []).filter((link) => link.label && link.visible !== false);
  const links: NavLink[] = [...autoLinks, ...customLinks];
  const secondaryLinks: NavLink[] = (config.secondaryLinks || []).filter(
    (link) => link.label && link.href && link.visible !== false
  );
  const ctaText = config.ctaText || "";
  const ctaLink = config.ctaLink || "";
  const ctaNextSteps = config.ctaNextSteps;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  const hasSecondaryNav = secondaryLinks.length > 0;

  // Animate mobile menu height
  useEffect(() => {
    const el = mobileMenuRef.current;
    if (!el) return;

    if (isMobileMenuOpen) {
      el.style.maxHeight = el.scrollHeight + "px";
    } else {
      el.style.maxHeight = el.scrollHeight + "px";
      // Force reflow so the browser registers the starting value
      el.offsetHeight;
      el.style.maxHeight = "0px";
    }
  }, [isMobileMenuOpen]);

  const handleTransitionEnd = useCallback(() => {
    const el = mobileMenuRef.current;
    if (!el) return;
    // When open, remove max-height so content can resize naturally
    if (isMobileMenuOpen) {
      el.style.maxHeight = "none";
    }
  }, [isMobileMenuOpen]);

  // Generate color palette from primary color
  const palette = useMemo(() => generateColorPalette(primaryColor || DEFAULT_PRIMARY_COLOR), [primaryColor]);

  return (
    <>
      <nav id="navbar" className="fixed top-0 w-full bg-white/95 backdrop-blur-sm z-40 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              {logoImage ? (
                <a href="#">
                  <img src={logoImage} alt="Logo" className="h-8 w-auto" />
                </a>
              ) : (
                <a href="#" className="text-xl font-bold text-gray-900">
                  Logo
                </a>
              )}
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {links.map((link, index) =>
                link.children && link.children.length > 0 ? (
                  <NavDropdown key={index} link={link} hasSecondaryNav={hasSecondaryNav} />
                ) : (
                  <a
                    key={index}
                    href={link.href}
                    className="text-gray-600 hover:text-gray-900 font-medium"
                    onClick={(e) => handleNavClick(e, link.href, hasSecondaryNav)}
                  >
                    {link.label}
                  </a>
                )
              )}
              {ctaText && ctaLink && (
                <SmartButton
                  href={ctaLink}
                  text={ctaText}
                  primaryColor={palette.primary}
                  eventId={eventId}
                  className="px-6 py-2 shadow-none!"
                  nextSteps={ctaNextSteps}
                />
              )}
            </div>

            {/* Mobile menu button */}
            <FetchButton
              variant="ghost"
              color="neutral"
              size="sm"
              iconOnly
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden touch-manipulation"
            >
              {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </FetchButton>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div
          ref={mobileMenuRef}
          onTransitionEnd={handleTransitionEnd}
          className="md:hidden bg-white border-t border-gray-100 overflow-hidden transition-[max-height] duration-300 ease-in-out"
          style={{ maxHeight: 0 }}
        >
          <div
            className="px-2 pt-2 pb-3 space-y-1 overflow-y-auto"
            style={{
              maxHeight: "calc(100vh - 4rem)",
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
              paddingBottom: "calc(0.75rem + env(safe-area-inset-bottom, 24px))",
            }}
          >
            {links.map((link, index) => (
              <div key={index}>
                {link.children && link.children.length > 0 && (link.headerOnly || !link.href) ? (
                  <span className="block px-3 py-2 text-gray-900 font-medium">{link.label}</span>
                ) : (
                  <a
                    href={link.href}
                    className="block px-3 py-2 text-gray-600 hover:text-gray-900 touch-manipulation min-h-[44px] flex items-center"
                    onClick={(e) => {
                      handleNavClick(e, link.href, hasSecondaryNav);
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    {link.label}
                  </a>
                )}
                {/* Mobile dropdown children as indented items */}
                {link.children &&
                  link.children.length > 0 &&
                  link.children.map((child, childIndex) => (
                    <a
                      key={childIndex}
                      href={child.href || "#"}
                      className="block pl-8 pr-3 py-2 text-sm text-gray-500 hover:text-gray-900 touch-manipulation min-h-[44px] flex items-center"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsMobileMenuOpen(false);
                        if (child.href) {
                          handleNavClick(e, child.href, hasSecondaryNav);
                        }
                      }}
                    >
                      {child.label}
                    </a>
                  ))}
              </div>
            ))}
            {ctaText && ctaLink && (
              <div className="px-3 py-2">
                <SmartButton
                  href={ctaLink}
                  text={ctaText}
                  primaryColor={palette.primary}
                  eventId={eventId}
                  className="w-full shadow-none!"
                  nextSteps={ctaNextSteps}
                />
              </div>
            )}
            {/* Secondary links in mobile menu */}
            {hasSecondaryNav && (
              <>
                <div className="border-t border-gray-100 my-2" />
                {secondaryLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link.href}
                    className="block px-3 py-2 text-sm text-gray-500 hover:text-gray-900 touch-manipulation min-h-[44px] flex items-center"
                    onClick={(e) => {
                      handleNavClick(e, link.href, hasSecondaryNav);
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    {link.label}
                  </a>
                ))}
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Secondary Navigation Bar */}
      {hasSecondaryNav && (
        <div className="fixed top-16 w-full bg-gray-50/95 backdrop-blur-sm z-30 border-b border-gray-200 hidden md:block">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-6 h-10">
              {secondaryLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.href}
                  className="text-sm text-gray-600 hover:text-gray-900 font-medium"
                  onClick={(e) => handleNavClick(e, link.href, hasSecondaryNav)}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
